/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ErrorType } from '../../api/errorHandling';
import {
 commonReducers,
 CommonState,
 initialCommonState,
} from '../_common/commonReducers';
import { AccessGroupsDTO, RolesDTO } from './accessGroupsTypes';

export interface AccessGroupsState extends CommonState {
 roles: RolesDTO[];
 allRoles: RolesDTO[];
 role: RolesDTO | null;
}

const initialState: AccessGroupsState = {
 ...initialCommonState,
 roles: [],
 allRoles: [],
 role: null,
};

const accessGroupsSlice = createSlice({
 name: 'accessGroups',
 initialState,
 reducers: {
  ...commonReducers,
  accessGroupsGetRolesStart(
   state: any,
   action: PayloadAction<{ accessGroupId: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, accessGroupsGetRoles: true },
   };
  },
  accessGroupsGetRolesSuccess(state: any, action: PayloadAction<RolesDTO[]>) {
   return {
    ...state,
    roles: action.payload || [],
    success: { ...state.success, accessGroupsGetRoles: true },
    loading: { ...state.loading, accessGroupsGetRoles: null },
    error: { ...(state.error || {}), accessGroupsGetRoles: null },
   };
  },
  accessGroupsGetRolesFailure(state: any, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsGetRoles: null },
    loading: { ...state.loading, accessGroupsGetRoles: null },
    error: {
     ...(state.error || {}),
     accessGroupsGetRoles: action.payload,
    },
   };
  },
  accessGroupsGetRolesTableStart(state: any, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, accessGroupsGetRolesTable: true },
   };
  },
  accessGroupsGetRolesTableSuccess(
   state: any,
   action: PayloadAction<RolesDTO[]>,
  ) {
   return {
    ...state,
    allRoles: action.payload || [],
    success: { ...state.success, accessGroupsGetRolesTable: true },
    loading: { ...state.loading, accessGroupsGetRolesTable: null },
    error: { ...(state.error || {}), accessGroupsGetRolesTable: null },
   };
  },
  accessGroupsGetRolesTableFailure(
   state: any,
   action: PayloadAction<ErrorType>,
  ) {
   return {
    ...state,
    success: { ...state.success, accessGroupsGetRolesTable: null },
    loading: { ...state.loading, accessGroupsGetRolesTable: null },
    error: {
     ...(state.error || {}),
     accessGroupsGetRolesTable: action.payload,
    },
   };
  },
  accessGroupsAddRoleStart(
   state: any,
   action: PayloadAction<{ accessGroupId: string; roles: RolesDTO[] }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, accessGroupsAddRole: true },
   };
  },
  accessGroupsAddRoleSuccess(state: any, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsAddRole: true },
    loading: { ...state.loading, accessGroupsAddRole: null },
    error: { ...(state.error || {}), accessGroupsAddRole: null },
   };
  },
  accessGroupsAddRoleFailure(state: any, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsAddRole: null },
    loading: { ...state.loading, accessGroupsAddRole: null },
    error: {
     ...(state.error || {}),
     accessGroupsAddRole: action.payload,
    },
   };
  },
  accessGroupsCreateRoleStart(state: any, action: PayloadAction<RolesDTO>) {
   return {
    ...state,
    loading: { ...state.loading, accessGroupsCreateRole: true },
   };
  },
  accessGroupsCreateRoleSuccess(state: any, action: PayloadAction<any>) {
   return {
    ...state,
    role: action.payload,
    success: { ...state.success, accessGroupsCreateRole: true },
    loading: { ...state.loading, accessGroupsCreateRole: null },
    error: { ...(state.error || {}), accessGroupsCreateRole: null },
   };
  },
  accessGroupsCreateRoleFailure(state: any, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsCreateRole: null },
    loading: { ...state.loading, accessGroupsCreateRole: null },
    error: {
     ...(state.error || {}),
     accessGroupsCreateRole: action.payload,
    },
   };
  },
  accessGroupsUpdateRoleStart(
   state: any,
   action: PayloadAction<{ id: string; data: RolesDTO }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, accessGroupsUpdateRole: true },
   };
  },
  accessGroupsUpdateRoleSuccess(state: any, action: PayloadAction<RolesDTO>) {
   return {
    ...state,
    role: action.payload,
    success: { ...state.success, accessGroupsUpdateRole: true },
    loading: { ...state.loading, accessGroupsUpdateRole: null },
    error: { ...(state.error || {}), accessGroupsUpdateRole: null },
   };
  },
  accessGroupsUpdateRoleFailure(state: any, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsUpdateRole: null },
    loading: { ...state.loading, accessGroupsUpdateRole: null },
    error: {
     ...(state.error || {}),
     accessGroupsUpdateRole: action.payload,
    },
   };
  },
  accessGroupsDeleteRoleStart(state: any, action: PayloadAction<RolesDTO>) {
   return {
    ...state,
    loading: { ...state.loading, accessGroupsDeleteRole: true },
   };
  },
  accessGroupsDeleteRoleSuccess(state: any) {
   return {
    ...state,
    success: { ...state.success, accessGroupsDeleteRole: true },
    loading: { ...state.loading, accessGroupsDeleteRole: null },
    error: { ...(state.error || {}), accessGroupsDeleteRole: null },
   };
  },
  accessGroupsDeleteRoleFailure(state: any, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsDeleteRole: null },
    loading: { ...state.loading, accessGroupsDeleteRole: null },
    error: {
     ...(state.error || {}),
     accessGroupsDeleteRole: action.payload,
    },
   };
  },
  accessGroupsRemoveRoleStart(
   state: any,
   action: PayloadAction<{ accessGroupId: string; roles: RolesDTO[] }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, accessGroupsRemoveRole: true },
   };
  },
  accessGroupsRemoveRoleSuccess(state: any, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsRemoveRole: true },
    loading: { ...state.loading, accessGroupsRemoveRole: null },
    error: { ...(state.error || {}), accessGroupsRemoveRole: null },
   };
  },
  accessGroupsRemoveRoleFailure(state: any, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsRemoveRole: null },
    loading: { ...state.loading, accessGroupsRemoveRole: null },
    error: {
     ...(state.error || {}),
     accessGroupsRemoveRole: action.payload,
    },
   };
  },
  accessGroupsDeleteGroupStart(
   state: any,
   action: PayloadAction<{ id: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, accessGroupsDeleteGroup: true },
   };
  },
  accessGroupsDeleteGroupSuccess(state: any) {
   return {
    ...state,
    success: { ...state.success, accessGroupsDeleteGroup: true },
    loading: { ...state.loading, accessGroupsDeleteGroup: null },
    error: { ...(state.error || {}), accessGroupsDeleteGroup: null },
   };
  },
  accessGroupsDeleteGroupFailure(state: any, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, accessGroupsDeleteGroup: null },
    loading: { ...state.loading, accessGroupsDeleteGroup: null },
    error: {
     ...(state.error || {}),
     accessGroupsDeleteGroup: action.payload,
    },
   };
  },
 },
});

export const {
 getTableStart,
 getTableSuccess,
 getTableFailure,
 getTableFinish,
 getItemStart,
 getItemSuccess,
 getItemFailure,
 getItemFinish,
 createItemStart,
 createItemSuccess,
 createItemFailure,
 createItemFinish,
 updateItemStart,
 updateItemSuccess,
 updateItemFailure,
 updateItemFinish,
 accessGroupsGetRolesStart,
 accessGroupsGetRolesSuccess,
 accessGroupsGetRolesFailure,
 accessGroupsGetRolesTableStart,
 accessGroupsGetRolesTableSuccess,
 accessGroupsGetRolesTableFailure,
 accessGroupsAddRoleStart,
 accessGroupsAddRoleSuccess,
 accessGroupsAddRoleFailure,
 accessGroupsCreateRoleStart,
 accessGroupsCreateRoleSuccess,
 accessGroupsCreateRoleFailure,
 accessGroupsUpdateRoleStart,
 accessGroupsUpdateRoleSuccess,
 accessGroupsUpdateRoleFailure,
 accessGroupsDeleteRoleStart,
 accessGroupsDeleteRoleSuccess,
 accessGroupsDeleteRoleFailure,
 accessGroupsRemoveRoleStart,
 accessGroupsRemoveRoleSuccess,
 accessGroupsRemoveRoleFailure,
 accessGroupsDeleteGroupStart,
 accessGroupsDeleteGroupSuccess,
 accessGroupsDeleteGroupFailure,
 deleteItemsStart,
 deleteItemsSuccess,
 deleteItemsFailure,
 deleteItemsFinish,

 deleteItemStart,
 deleteItemSuccess,
 deleteItemFailure,
 deleteItemFinish,
} = accessGroupsSlice.actions;

export default accessGroupsSlice;

